import {
  DatePicker,
  DateTimePicker as MuiDateTimePicker,
} from "@mui/x-date-pickers";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";

import styles from "./DateRangePicker.module.css";

/**
 * A usable window of whole days: both bounds present, start not after end.
 * Anything else is `null` — never a range with a hole in it.
 *
 * Strings, not `Dayjs`: a library object in a primitive's public API leaks the
 * dependency into every consumer's types and is a fresh identity on every
 * construction. These are also exactly what the dashboards domain stores, so a
 * picked range needs no adapter on its way to `TimeRange.fromDates`.
 */
export interface DateRange {
  /** `YYYY-MM-DD`. */
  start: string;
  /** `YYYY-MM-DD`, inclusive. */
  end: string;
}

/** A usable window of instants. See {@link DateRange}; `start` and `end` are
 *  full ISO 8601 rather than calendar dates. */
export interface DateTimeRange {
  start: string;
  end: string;
}

interface RangePickerProps<T> {
  /**
   * Seeds the fields at mount; later changes are ignored, so remount with a
   * `key` to reset. A partial is allowed — a caller mid-edit may know only one
   * bound, and dropping it would lose that work on every operator change.
   * Deliberately looser than `onChange`, which still only ever reports a
   * complete, ordered window or `null`.
   */
  value: Partial<T> | null;
  /**
   * Fires on every edit. `null` means what the fields currently hold is
   * incomplete or reversed, so a consumer gating a commit can test for it
   * rather than validating the range itself.
   */
  onChange: (range: T | null) => void;
  /** Marks both fields invalid, for an error shown beside them. */
  invalid?: boolean;
  className?: string;
}

/** Empty string reads as absent rather than as a bound to parse: a caller whose
 *  storage is a fixed-arity list writes `""` into a slot it has not filled. */
function seedField(iso: string | undefined, parse: (iso: string) => Dayjs) {
  return iso ? parse(iso) : null;
}

export type DateRangePickerProps = RangePickerProps<DateRange>;
export type DateTimeRangePickerProps = RangePickerProps<DateTimeRange>;

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Pick a window of whole days.
 *
 * The fields stack when the row cannot hold both, so the control adapts to
 * whatever width it is given without a breakpoint to tune.
 */
export function DateRangePicker({
  value,
  onChange,
  invalid,
  className,
}: DateRangePickerProps) {
  return (
    <RangeFields
      value={value}
      onChange={onChange}
      invalid={invalid}
      className={className}
      startLabel="Start date"
      endLabel="End date"
      parse={(iso) => dayjs.utc(iso, DATE_FORMAT, true)}
      serialize={(d) => d.format(DATE_FORMAT)}
      renderField={(props) => <DatePicker {...props} />}
    />
  );
}

/** Pick a window of instants, when time of day is load-bearing. See
 *  {@link DateRangePicker}. */
export function DateTimeRangePicker({
  value,
  onChange,
  invalid,
  className,
}: DateTimeRangePickerProps) {
  return (
    <RangeFields
      value={value}
      onChange={onChange}
      invalid={invalid}
      className={className}
      startLabel="Start date & time"
      endLabel="End date & time"
      parse={(iso) => dayjs(iso)}
      serialize={(d) => d.toISOString()}
      renderField={(props) => <MuiDateTimePicker {...props} />}
    />
  );
}

interface FieldProps {
  label: string;
  value: Dayjs | null;
  onChange: (next: Dayjs | null) => void;
  slotProps: {
    textField: { size: "small"; fullWidth: true; error: boolean };
  };
}

interface RangeFieldsProps<T> extends RangePickerProps<T> {
  startLabel: string;
  endLabel: string;
  parse: (iso: string) => Dayjs;
  serialize: (value: Dayjs) => string;
  renderField: (props: FieldProps) => React.ReactElement;
}

/**
 * The shared body behind both pickers: two MUI fields and the rule that turns
 * them into a range or into `null`.
 *
 * Two independent fields rather than one range calendar because the free
 * `@mui/x-date-pickers` ships no range component — those are Pro. The
 * trade-off is no shading between the bounds; what it buys is a real text
 * input per field, so a date can be typed, pasted, and copied between them.
 */
function RangeFields<T extends { start: string; end: string }>({
  value,
  onChange,
  invalid = false,
  className,
  startLabel,
  endLabel,
  parse,
  serialize,
  renderField,
}: RangeFieldsProps<T>) {
  // Seeded per field, not per range, so one bound still shows when the other is
  // missing — what carries "after X" into "between X and …".
  const [start, setStart] = React.useState<Dayjs | null>(() =>
    seedField(value?.start, parse),
  );
  const [end, setEnd] = React.useState<Dayjs | null>(() =>
    seedField(value?.end, parse),
  );

  const emit = (nextStart: Dayjs | null, nextEnd: Dayjs | null) => {
    // Written as a guard rather than a boolean so both bounds narrow to
    // non-null for `serialize` below.
    if (
      nextStart !== null &&
      nextEnd !== null &&
      nextStart.isValid() &&
      nextEnd.isValid() &&
      !nextStart.isAfter(nextEnd)
    ) {
      onChange({ start: serialize(nextStart), end: serialize(nextEnd) } as T);
      return;
    }
    onChange(null);
  };

  // Only a REVERSED window is announced. An incomplete one is equally
  // unusable, but the user is mid-entry — telling them the range is wrong
  // before they have finished typing it would be noise.
  const reversed =
    start !== null &&
    end !== null &&
    start.isValid() &&
    end.isValid() &&
    start.isAfter(end);

  // A reversed window is the picker's own finding, so it marks the fields
  // whatever the caller passed: the announcement below and the outline are one
  // state, and an announcement pointing at unmarked fields is the bug.
  const slotProps = {
    textField: { size: "small", fullWidth: true, error: invalid || reversed },
  } as const;

  return (
    <div
      className={classNames(
        styles.fields,
        (invalid || reversed) && styles.invalid,
        className,
      )}
    >
      <div className={styles.field}>
        {renderField({
          label: startLabel,
          value: start,
          onChange: (next) => {
            setStart(next);
            emit(next, end);
          },
          slotProps,
        })}
      </div>
      <div className={styles.field}>
        {renderField({
          label: endLabel,
          value: end,
          onChange: (next) => {
            setEnd(next);
            emit(start, next);
          },
          slotProps,
        })}
      </div>
      {reversed ? (
        <p className={styles.error} role="alert">
          The end must not be before the start.
        </p>
      ) : null}
    </div>
  );
}
